import React, { useState, useEffect, useRef } from 'react';
import { 

  Brain, FileText, MessageSquare, Calculator, FileCheck,
  Send, Loader2, Sparkles, Target, ChevronRight,
  Users, TrendingUp, AlertCircle, CheckCircle, X,
  Download, Upload, RefreshCw, Zap, Search,
  Building2, Globe, Factory, Truck, Droplets
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

// Service Tab Component
const ServiceTab = ({ service, isActive, onClick }) => {
  const icons = {
    llm_extraction: FileText,
    narrative_generation: Sparkles,
    ask_disclosure_os: MessageSquare,
    emission_factors: Calculator,
    evidence_management: FileCheck
  };
  const Icon = icons[service.id] || Brain;
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all w-full text-left ${
        isActive 
          ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-500' 
          : 'bg-white hover:bg-slate-50 border border-slate-200'
      }`}
      data-testid={`service-tab-${service.id}`}
    >
      <Icon size={20} />
      <div className="flex-1">
        <div className="font-medium">{service.name}</div>
        <div className="text-xs opacity-70">{service.gap_fixed}</div>
      </div>
      <ChevronRight size={16} className={isActive ? 'rotate-90' : ''} />
    </button>
  );
};

// Gap 1: LLM Document Extraction Panel
const LLMExtractionPanel = () => {
  const [text, setText] = useState('');
  const [docType, setDocType] = useState('sustainability_report');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const extractWithLLM = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai/extract-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text, document_type: docType })
      });
      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('LLM Extraction error:', err);
    }
    setLoading(false);
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Brain className="text-white" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">GPT-5.2 Powered Extraction</h3>
            <p className="text-sm text-slate-600">
              Intelligent extraction understands context, tables, and complex narratives
            </p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-medium text-slate-700 mb-2">Document Type</label>
          <select 
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="sustainability_report">Sustainability Report</option>
            <option value="annual_report">Annual Report</option>
            <option value="cdp_response">CDP Response</option>
            <option value="supplier_audit">Supplier Audit</option>
            <option value="invoice">Utility Invoice</option>
          </select>
        </div>
        <div className="col-span-3">
          <label className="block text-sm font-medium text-slate-700 mb-2">Document Text</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste document content here. The AI will extract all ESG metrics, identify frameworks, and assess data quality..."
            className="w-full h-32 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
            data-testid="llm-text-input"
          />
        </div>
      </div>
      
      <button
        onClick={extractWithLLM}
        disabled={loading || !text.trim()}
        className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="llm-extract-btn"
      >
        {loading ? <Loader2 className="animate-spin" size={20} /> : <Brain size={20} />}
        {loading ? 'AI is Analyzing...' : 'Extract with GPT-5.2'}
      </button>
      
      {result && !result.error && (
        <div className="space-y-4 bg-white rounded-xl border border-slate-200 p-6" data-testid="llm-result">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-slate-900">AI Extraction Results</h4>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
              {result.metrics_count} metrics found
            </span>
          </div>
          
          {result.summary && (
            <p className="text-slate-600 bg-slate-50 p-4 rounded-lg">{result.summary}</p>
          )}
          
          {result.frameworks_detected?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {result.frameworks_detected.map((fw, idx) => (
                <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                  {fw}
                </span>
              ))}
            </div>
          )}
          
          {result.extracted_metrics?.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {result.extracted_metrics.map((metric, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-medium text-slate-900">
                      {metric.metric_name?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    {metric.framework_reference && (
                      <span className="ml-2 text-xs text-slate-500">({metric.framework_reference})</span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="font-semibold text-emerald-600">
                      {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value} {metric.unit}
                    </span>
                    <p className="text-xs text-slate-400">
                      Confidence: {Math.round((metric.confidence || 0.8) * 100)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Gap 2: AI Report Writer Panel
const ReportWriterPanel = () => {
  const [audience, setAudience] = useState('investor');
  const [reportType, setReportType] = useState('annual_sustainability_report');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generateNarrative = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai/generate-narrative-from-db?report_type=${reportType}&audience=${audience}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Narrative generation error:', err);
    }
    setLoading(false);
  };
  
  const audienceDescriptions = {
    investor: "Focus on financial materiality, risks, and competitive positioning",
    regulator: "Precise compliance language, methodology, audit-ready documentation",
    public: "Accessible, engaging narratives with human impact stories",
    executive: "Concise summaries with strategic implications and action items"
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <Sparkles className="text-white" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">AI Report Writer</h3>
            <p className="text-sm text-slate-600">
              Generate compelling ESG narratives tailored to your audience
            </p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Target Audience</label>
          <div className="space-y-2">
            {['investor', 'regulator', 'public', 'executive'].map((aud) => (
              <button
                key={aud}
                onClick={() => setAudience(aud)}
                className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                  audience === aud 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                <div className="font-medium capitalize">{aud}</div>
                <div className="text-xs text-slate-500">{audienceDescriptions[aud]}</div>
              </button>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg mb-4"
          >
            <option value="annual_sustainability_report">Annual Sustainability Report</option>
            <option value="csrd_disclosure">CSRD Disclosure</option>
            <option value="tcfd_report">TCFD Report</option>
            <option value="cdp_response">CDP Response</option>
            <option value="quarterly_update">Quarterly Update</option>
          </select>
          
          <button
            onClick={generateNarrative}
            disabled={loading}
            className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="generate-narrative-btn"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
            {loading ? 'Writing Report...' : 'Generate Narrative'}
          </button>
        </div>
      </div>
      
      {result && !result.error && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="narrative-result">
          <h4 className="font-semibold text-xl text-slate-900">{result.report_title}</h4>
          
          {result.sections?.map((section, idx) => (
            <div key={idx} className="border-l-4 border-purple-500 pl-4 py-2">
              <h5 className="font-semibold text-slate-800">{section.headline || section.section_name}</h5>
              <p className="text-slate-600 mt-2 whitespace-pre-wrap">{section.narrative}</p>
              {section.key_metrics?.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {section.key_metrics.map((metric, mIdx) => (
                    <span key={mIdx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                      {metric}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {result.key_messages?.length > 0 && (
            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
              <h5 className="font-medium text-purple-800 mb-2">Key Messages</h5>
              <ul className="space-y-1">
                {result.key_messages.map((msg, idx) => (
                  <li key={idx} className="text-sm text-purple-700">• {msg}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {result?.error && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800">{result.error}</p>
        </div>
      )}
    </div>
  );
};

// Gap 3: Ask Disclosure OS Panel
const AskDisclosureOSPanel = () => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const askQuestion = async () => {
    if (!question.trim()) return;
    
    const userMessage = { type: 'user', content: question };
    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          question: userMessage.content,
          session_id: sessionId,
          include_context: true
        })
      });
      const data = await response.json();
      
      if (!sessionId) setSessionId(data.session_id);
      
      setMessages(prev => [...prev, { 
        type: 'assistant', 
        content: data.answer,
        sources: data.data_sources_consulted
      }]);
    } catch (err) {
      console.error('Query error:', err);
      setMessages(prev => [...prev, { 
        type: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        error: true
      }]);
    }
    setLoading(false);
  };
  
  const suggestedQuestions = [
    "What are our current Scope 1 emissions?",
    "How do we compare to industry benchmarks?",
    "What are our biggest compliance risks?",
    "Show me our progress toward net zero",
    "Which suppliers have the highest ESG risk?"
  ];
  
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
            <MessageSquare className="text-white" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Ask Disclosure OS</h3>
            <p className="text-sm text-slate-600">
              Ask questions about your ESG data in natural language
            </p>
          </div>
        </div>
      </div>
      
      {/* Suggested Questions */}
      {messages.length === 0 && (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">Try asking:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.map((q, idx) => (
              <button
                key={idx}
                onClick={() => setQuestion(q)}
                className="px-3 py-2 bg-slate-100 hover:bg-emerald-100 text-slate-700 rounded-lg text-sm transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Chat Messages */}
      <div className="h-96 overflow-y-auto space-y-4 bg-slate-50 rounded-xl p-4" data-testid="chat-messages">
        {messages.map((msg, idx) => (
          <div 
            key={idx} 
            className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[80%] p-4 rounded-xl ${
              msg.type === 'user' 
                ? 'bg-emerald-600 text-white' 
                : msg.error 
                  ? 'bg-red-100 text-red-800'
                  : 'bg-white border border-slate-200'
            }`}>
              <p className="whitespace-pre-wrap">{msg.content}</p>
              {msg.sources?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    Data sources: {msg.sources.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 p-4 rounded-xl">
              <Loader2 className="animate-spin text-emerald-600" size={20} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && askQuestion()}
          placeholder="Ask about your ESG data..."
          className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          data-testid="ask-input"
        />
        <button
          onClick={askQuestion}
          disabled={loading || !question.trim()}
          className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          data-testid="ask-btn"
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
};

// Gap 4: Emission Calculator Panel
const EmissionCalculatorPanel = () => {
  const [activityType, setActivityType] = useState('electricity');
  const [activityValue, setActivityValue] = useState('');
  const [country, setCountry] = useState('US');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const activityTypes = [
    { value: 'electricity', label: 'Electricity', unit: 'kWh', icon: Zap },
    { value: 'natural_gas', label: 'Natural Gas', unit: 'kWh', icon: Factory },
    { value: 'diesel', label: 'Diesel', unit: 'liters', icon: Truck },
    { value: 'car_travel', label: 'Car Travel', unit: 'km', icon: Truck },
    { value: 'air_travel', label: 'Air Travel', unit: 'km', icon: Globe },
    { value: 'water_supply', label: 'Water', unit: 'm³', icon: Droplets },
  ];
  
  const calculateEmissions = async () => {
    if (!activityValue) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const activity = activityTypes.find(a => a.value === activityType);
      const response = await fetch(`${API}/api/ai/calculate-emissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          activity_type: activityType,
          activity_value: parseFloat(activityValue),
          activity_unit: activity?.unit || 'unit',
          country: country
        })
      });
      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Calculation error:', err);
    }
    setLoading(false);
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
            <Calculator className="text-white" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Emission Factor Calculator</h3>
            <p className="text-sm text-slate-600">
              Calculate emissions using DEFRA/EPA/GHG Protocol factors
            </p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        {activityTypes.map((activity) => {
          const Icon = activity.icon;
          return (
            <button
              key={activity.value}
              onClick={() => setActivityType(activity.value)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                activityType === activity.value 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-slate-200 hover:border-orange-300'
              }`}
            >
              <Icon size={24} className={activityType === activity.value ? 'text-orange-600' : 'text-slate-400'} />
              <div className="font-medium mt-2">{activity.label}</div>
              <div className="text-xs text-slate-500">{activity.unit}</div>
            </button>
          );
        })}
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Amount</label>
          <input
            type="number"
            value={activityValue}
            onChange={(e) => setActivityValue(e.target.value)}
            placeholder="Enter amount"
            className="w-full px-4 py-2 border border-slate-300 rounded-lg"
            data-testid="activity-value-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Country</label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg"
          >
            <option value="US">United States</option>
            <option value="UK">United Kingdom</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="CN">China</option>
            <option value="IN">India</option>
            <option value="JP">Japan</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={calculateEmissions}
            disabled={loading || !activityValue}
            className="w-full py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="calculate-btn"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Calculator size={20} />}
            Calculate
          </button>
        </div>
      </div>
      
      {result && !result.error && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="calculation-result">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500">Activity</p>
              <p className="text-lg font-semibold">{result.activity_value?.toLocaleString()} {result.activity_unit}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Emission Factor</p>
              <p className="text-lg font-semibold">{result.emission_factor} {result.emission_factor_unit}</p>
            </div>
            <div className="col-span-2 p-4 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-600">Calculated Emissions</p>
              <p className="text-3xl font-bold text-emerald-700">{result.emissions_tco2e?.toLocaleString()} tCO2e</p>
              <p className="text-sm text-slate-500 mt-1">({result.emissions_kg_co2e?.toLocaleString()} kg CO2e)</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4">Source: {result.source} | Methodology: {result.methodology}</p>
        </div>
      )}
    </div>
  );
};

// Gap 5: Evidence Management Panel
const EvidenceManagementPanel = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const loadReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai/evidence/report?report_type=full`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setReport(data);
    } catch (err) {
      console.error('Error loading report:', err);
    }
    setLoading(false);
  };
  
  useEffect(() => {
    loadReport();
  }, []);
  
  const getReadinessColor = (readiness) => {
    const colors = {
      high: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-red-100 text-red-700'
    };
    return colors[readiness] || colors.low;
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-600 rounded-lg flex items-center justify-center">
            <FileCheck className="text-white" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Evidence Management</h3>
            <p className="text-sm text-slate-600">
              Link ESG claims to source documents for audit readiness
            </p>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-cyan-600" size={32} />
        </div>
      ) : report ? (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Total Evidence</p>
              <p className="text-2xl font-bold text-slate-900">{report.total_evidence_items}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Verified</p>
              <p className="text-2xl font-bold text-green-600">{report.verification_summary?.verified || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{report.verification_summary?.pending || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Audit Readiness</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getReadinessColor(report.audit_readiness)}`}>
                {report.audit_readiness?.toUpperCase()}
              </span>
            </div>
          </div>
          
          {/* Evidence Type Breakdown */}
          {Object.keys(report.evidence_type_breakdown || {}).length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h4 className="font-semibold text-slate-900 mb-4">Evidence by Type</h4>
              <div className="space-y-3">
                {Object.entries(report.evidence_type_breakdown).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="capitalize text-slate-600">{type.replace('_', ' ')}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-cyan-500 rounded-full" 
                          style={{ width: `${(count / report.total_evidence_items) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-700 w-8">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Metrics Coverage */}
          {report.metrics_with_evidence?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h4 className="font-semibold text-slate-900 mb-4">Metrics with Evidence</h4>
              <div className="flex flex-wrap gap-2">
                {report.metrics_with_evidence.map((metric, idx) => (
                  <span key={idx} className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm">
                    {metric}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <button
            onClick={loadReport}
            className="w-full py-2 border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50 flex items-center justify-center gap-2"
          >
            <RefreshCw size={18} />
            Refresh Report
          </button>
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500">
          <FileCheck size={48} className="mx-auto mb-4 opacity-50" />
          <p>No evidence data available yet.</p>
          <p className="text-sm mt-2">Evidence will be created as you extract and link documents to metrics.</p>
        </div>
      )}
    </div>
  );
};

// Main Component
const AIPoweredServices = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchCapabilities();
  }, []);
  
  const fetchCapabilities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai/capabilities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setServices(data.ai_services || []);
      if (data.ai_services?.length > 0) {
        setSelectedService(data.ai_services[0]);
      }
    } catch (err) {
      console.error('Error fetching capabilities:', err);
      // Fallback services
      const fallback = [
        { id: 'llm_extraction', name: 'LLM Document Extraction', gap_fixed: 'Gap 1' },
        { id: 'narrative_generation', name: 'AI Report Writer', gap_fixed: 'Gap 2' },
        { id: 'ask_disclosure_os', name: 'Ask Disclosure OS', gap_fixed: 'Gap 3' },
        { id: 'emission_factors', name: 'Emission Calculator', gap_fixed: 'Gap 4' },
        { id: 'evidence_management', name: 'Evidence Management', gap_fixed: 'Gap 5' }
      ];
      setServices(fallback);
      setSelectedService(fallback[0]);
    }
    setLoading(false);
  };
  
  const renderServicePanel = () => {
    if (!selectedService) return null;
    
    switch (selectedService.id) {
      case 'llm_extraction':
        return <LLMExtractionPanel />;
      case 'narrative_generation':
        return <ReportWriterPanel />;
      case 'ask_disclosure_os':
        return <AskDisclosureOSPanel />;
      case 'emission_factors':
        return <EmissionCalculatorPanel />;
      case 'evidence_management':
        return <EvidenceManagementPanel />;
      default:
        return <div className="text-center py-12 text-slate-500">Select a service</div>;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-emerald-600" size={48} />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="ai-powered-services">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-xl p-8 text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <Brain size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-bold">AI-Powered ESG Services</h1>
            <p className="text-emerald-100 mt-1">
              GPT-5.2 powered document extraction, report writing, conversational queries, and more
            </p>
          </div>
        </div>
        <div className="flex gap-4 mt-6">
          <div className="px-4 py-2 bg-white/20 rounded-lg text-sm">
            ✓ 5 Critical Gaps Fixed
          </div>
          <div className="px-4 py-2 bg-white/20 rounded-lg text-sm">
            ✓ LLM-Powered Intelligence
          </div>
          <div className="px-4 py-2 bg-white/20 rounded-lg text-sm">
            ✓ Audit-Ready Evidence
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-6">
        {/* Service Tabs */}
        <div className="space-y-3">
          {services.map(service => (
            <ServiceTab 
              key={service.id}
              service={service}
              isActive={selectedService?.id === service.id}
              onClick={() => setSelectedService(service)}
            />
          ))}
        </div>
        
        {/* Service Panel */}
        <div className="col-span-3 bg-white rounded-xl border border-slate-200 p-6">
          {renderServicePanel()}
        </div>
      </div>
    </div>
  );
};

export default AIPoweredServices;
